import { localeParentMap } from '@eventbrite/webapp-locales/dist/utils';
import { DEFAULT_LOCALE_UNDERSCORE, getFinalLocale } from '../src/lib/localeInfo';

type LocaleMessages = Record<string, string>;

function normalizeToUnderscoreLocale(locale?: string): string {
    // Global replace: multi-subtag locales (e.g. zh-Hant-TW) have more than one separator,
    // and a string-pattern replace only swaps the first, producing a malformed locale and a
    // wrong ./translations/*.json import path.
    return getFinalLocale(locale).replace(/-/g, '_');
}

/** BCP47-ish locale (dash) for react-intl, e.g. `en-US`. */
export function formatLocale(locale?: string): string {
    return normalizeToUnderscoreLocale(locale).replace(/_/g, '-');
}

// Catalog lookup with PARENT-LOCALE FALLBACK (same pattern as
// consumer-onboarding-webapp): the pull script writes one file per parent
// locale (empty/404 locales are skipped), so a country TLD like de_AT must
// resolve to its parent de_DE's catalog before en_US. Same chain as eb-ui's
// getParentLocale → en_US.
function catalogChain(locale?: string): string[] {
    const normalized = normalizeToUnderscoreLocale(locale);
    const chain: string[] = [];
    let current: string | undefined = normalized;
    while (current && !chain.includes(current)) {
        chain.push(current);
        current = localeParentMap[current];
    }
    if (!chain.includes(DEFAULT_LOCALE_UNDERSCORE)) {
        chain.push(DEFAULT_LOCALE_UNDERSCORE);
    }
    return chain;
}

export async function getLocaleMessages(locale?: string): Promise<LocaleMessages> {
    // Per-key MERGE over the chain (most-specific wins): a sparse country
    // catalog overlays its parent's full catalog, not replaces it — else
    // missing keys fall back to English instead of the parent language.
    // Chain order is most→least specific, so merge least-specific first and
    // let each more specific file overlay its keys. Untranslated keys render
    // their source text via gettext's key-is-fallback behaviour.
    const merged: LocaleMessages = {};
    for (const candidate of [...catalogChain(locale)].reverse()) {
        try {
            const m = await import(`./translations/${candidate}.json`);
            Object.assign(merged, (m.default ?? m) as LocaleMessages);
        } catch {
            // file absent for this chain link — the next (parent) link covers it
        }
    }
    return merged;
}
